<template>
  <section class="container mx-auto px-4 md:px-8 py-8">
    <h1 class="text-2xl md:text-3xl font-bold mb-6">Мій профіль</h1>

    <div v-if="pError" class="mb-4 text-sm text-red-600">{{ pError }}</div>

    <div class="grid lg:grid-cols-2 gap-8">
      <!-- Ліва колонка: профіль -->
      <form
        v-if="profile"
        ref="leftCardRef"
        class="border rounded-2xl p-5 space-y-4"
        @submit.prevent="save"
      >
        <h2 class="text-lg font-semibold">Особисті дані</h2>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 mb-1"
              >Прізвище та імʼя</label
            >
            <input
              v-model="profile.displayName"
              class="w-full border rounded-lg px-3 py-2"
              placeholder="Прізвище Імʼя"
              @input="nameTouched = true"
              @blur="nameTouched = true"
            />
            <!--
              `nameTouched` тут уже був і виставлявся на @input/@blur, але
              підказка його ігнорувала — тож свіжо відкритий профіль із
              незаповненим ПІБ одразу зустрічав червоним текстом.
            -->
            <p
              v-if="nameTouched && !isNameValid"
              class="text-xs text-red-600 mt-1"
            >
              {{ FULL_NAME_HINT }}
            </p>
          </div>

          <div>
            <label class="block text-sm text-gray-600 mb-1">Телефон</label>
            <!--
              Це ВЛАСНИЙ номер людини — той самий, яким вона входить по SMS.
              Окремого «телефону профілю» більше немає: `profile.phone` став
              дзеркалом `auth.user.phoneNumber`. Контакт ОТРИМУВАЧА (може бути
              чужим) живе окремо, у shipping.phone, і вводиться в чекауті.
              Поле редаговане: без цього змінити номер не було як.
            -->
            <input
              v-model="form.phone"
              v-phone-ua
              type="tel"
              autocomplete="tel"
              placeholder="+380 XX XXX XX XX"
              class="w-full border rounded-lg px-3 py-2"
              @input="phoneTouched = true"
              @blur="phoneTouched = true"
            />
            <p
              v-if="phoneTouched && form.phone && !isPhoneValid"
              class="text-xs text-red-600 mt-1"
            >
              {{ PHONE_FORMAT_HINT }}
            </p>

            <!--
              Гейта «спершу збережіть зміни» тут БІЛЬШЕ НЕМАЄ. Він був подвійно
              зайвий: `onOtpVerified` і так викликає `persistProfile()`, а
              кнопка «Зберегти» вимикається при невалідному ПІБ — тож людина з
              іменем в одне слово не могла ні зберегти, ні підтвердити номер.
              Номер зберігається САМЕ фактом підтвердження.
            -->
            <p v-if="isPhoneVerified" class="text-xs text-green-700 mt-1">
              Номер підтверджено — ви входите по ньому через SMS.
            </p>
            <div v-else-if="isPhoneValid" class="mt-1">
              <button
                type="button"
                class="text-xs underline disabled:no-underline disabled:opacity-60"
                :disabled="isSaving"
                @click="startPhoneVerification"
              >
                Підтвердити номер для входу по SMS
              </button>
            </div>
            <p v-else-if="!form.phone" class="text-xs text-gray-500 mt-1">
              Номер не підтверджений. Введіть його, щоб входити по SMS.
            </p>
          </div>
        </div>

        <div>
          <label class="block text-sm text-gray-600 mb-1">Instagram</label>
          <input
            v-model="profile.instagram"
            class="w-full border rounded-lg px-3 py-2"
            placeholder="@mulberry"
          />
        </div>

        <!--
          Email і пароль: тільки для тих, хто має email-акаунт. Email показаний
          НЕ редагованим навмисно — його зміна в Firebase вимагає повторного
          підтвердження і свіжого входу, це окрема історія. Зміна пароля йде
          тим самим листом, що й «Забули пароль?» на /login.
        -->
        <div v-if="accountEmail">
          <label class="block text-sm text-gray-600 mb-1">Email</label>
          <input
            :value="accountEmail"
            disabled
            class="w-full border rounded-lg px-3 py-2 bg-gray-50 text-gray-600"
          />
          <div class="mt-1 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              class="text-xs underline disabled:no-underline disabled:opacity-60"
              :disabled="resetSending"
              @click="requestPasswordReset"
            >
              {{ resetSending ? "Надсилаємо…" : "Змінити пароль" }}
            </button>
            <span v-if="resetNote" class="text-xs text-green-700">{{
              resetNote
            }}</span>
            <span v-else-if="resetError" class="text-xs text-red-600">{{
              resetError
            }}</span>
          </div>
        </div>

        <!--
          Телефонний акаунт без email. Це LINK до поточного користувача, а не
          нова реєстрація: інакше людина завела б ДРУГИЙ акаунт і втратила
          доступ до власних замовлень.
        -->
        <div v-else>
          <label class="block text-sm text-gray-600 mb-1">Email і пароль</label>
          <p class="text-xs text-gray-500 mb-2">
            Додайте їх, щоб входити в цей самий профіль ще й через email — з
            усіма замовленнями. Телефонний вхід продовжить працювати.
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              v-model.trim="linkEmail"
              type="email"
              autocomplete="email"
              placeholder="Email"
              class="w-full border rounded-lg px-3 py-2"
            />
            <input
              v-model="linkPassword"
              type="password"
              autocomplete="new-password"
              placeholder="Пароль (мін. 6 символів)"
              class="w-full border rounded-lg px-3 py-2"
            />
          </div>
          <div class="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              class="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 disabled:opacity-60"
              :disabled="linkSending || !canLinkEmail"
              @click="submitLinkEmail"
            >
              {{ linkSending ? "Додаємо…" : "Додати email" }}
            </button>
            <span v-if="linkNote" class="text-xs text-green-700">{{
              linkNote
            }}</span>
            <span v-else-if="linkError" class="text-xs text-red-600">{{
              linkError
            }}</span>
          </div>
        </div>

        <NovaPoshtaDeliveryForm
          v-if="deliveryReady"
          v-model="delivery"
          @valid-change="deliveryValid = $event"
        />

        <div class="pt-2 flex items-center">
          <button
            class="px-5 py-2 rounded-xl font-semibold bg-pink-200 text-black hover:bg-pink-200/80 disabled:opacity-60 inline-flex items-center gap-2"
            :disabled="
              isSaving ||
              pLoading ||
              !profile ||
              !isNameValid ||
              (deliveryReady && deliveryChanged && !deliveryValid) ||
              !isDirty
            "
          >
            <Loader2 v-if="isSaving || pLoading" class="w-4 h-4 animate-spin" />
            {{ isSaving || pLoading ? "Зберігаємо…" : "Зберегти" }}
          </button>

          <button
            type="button"
            class="ml-auto px-4 py-2 rounded-xl border hover:bg-gray-50 text-sm md:text-base"
            @click="doLogout"
          >
            Вийти
          </button>
        </div>
      </form>

      <div v-else class="border rounded-2xl p-5 space-y-3">
        <div class="h-5 bg-gray-100 rounded w-40"></div>
        <div class="h-10 bg-gray-100 rounded"></div>
        <div class="h-10 bg-gray-100 rounded"></div>
      </div>

      <div
        class="border rounded-2xl p-5 flex flex-col"
        :style="{ maxHeight: rightMaxHeight }"
      >
        <ProfileBuyHistory class="min-h-0 flex-1" />
      </div>
    </div>

    <!-- OTP-попап -->
    <PhoneOtpModal
      v-if="otp.open"
      :phone-e164="otp.phoneE164"
      :masked="form.phone"
      @verified="onOtpVerified"
      @cancel="onOtpCancelled"
    />
  </section>
</template>

<script setup>
import {
  onMounted,
  onBeforeUnmount,
  nextTick,
  ref,
  computed,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import { Loader2 } from "lucide-vue-next";
import NovaPoshtaDeliveryForm from "~/components/common/NovaPoshtaDeliveryForm.vue";
import ProfileBuyHistory from "~/components/profile/BuyHistory.vue";
import PhoneOtpModal from "~/components/auth/PhoneOtpModal.vue";
import { useAuthStore } from "~/stores/auth";
import { useUserProfile } from "~/composables/useUserProfile";
import {
  maskFromE164,
  toE164,
  isValidUaPhone,
  PHONE_FORMAT_HINT,
} from "~/utils/phone";
import { httpsCallable } from "firebase/functions";
import { useNuxtApp } from "#app";
import { useUiStore } from "~/stores/ui";

const ui = useUiStore();

definePageMeta({ middleware: "auth", ssr: false });

// Свого title сторінка не мала — і діставала дефолт із nuxt.config
// («mulberry’ss — інтернет-магазин»), у якому бренд уже є, тож titleTemplate
// ("Мій профіль — mulberry'ss") наліплював його вдруге. Після клієнтської навігації
// дефолт губився зовсім і у вкладці лишалось голе «— mulberry'ss».
// Бренд тут НЕ пишемо: його додає шаблон.
useHead({ title: "Мій профіль" });
ui.start("profile-load");

const router = useRouter();
const auth = useAuthStore();
const { $functions } = useNuxtApp();

// локальна форма (тільки телефон — решта напряму з profile)
const form = ref({ phone: "" });
const phoneTouched = ref(false);
const nameTouched = ref(false);

const isNameValid = computed(() => isFullNameUa(profile.value?.displayName));
const isPhoneValid = computed(() => isValidUaPhone(form.value.phone));

const authPhoneE164 = computed(() => auth.user?.phoneNumber || "");

/**
 * Свіжопідтверджений номер. Потрібен окремо, бо `updatePhoneNumber` міняє
 * об'єкт User НА МІСЦІ, а стор присвоює `user.value = u` тим самим
 * посиланням — Vue такої зміни не бачить, і статус лишався б застарілим до
 * перезавантаження сторінки.
 */
const justVerifiedE164 = ref("");

const isPhoneVerified = computed(() => {
  const e164 = toE164(form.value.phone);
  if (!e164) return false;
  return e164 === authPhoneE164.value || e164 === justVerifiedE164.value;
});

// профіль / збереження
const {
  profile,
  loading: pLoading,
  error: pError,
  fetchProfile,
  saveProfile,
} = useUserProfile();

// доставка
const deliveryReady = ref(false);
const deliveryValid = ref(false);
const delivery = ref({
  deliveryType: "np_branch",
  city: "",
  cityRef: "",
  line1: "",
  warehouseRef: "",
  warehouseName: "",
});

// 🔵 базовий знімок доставки + прапорець змін
const initialDeliverySnapshot = ref("");
const deliveryChanged = computed(
  () => JSON.stringify(delivery.value) !== initialDeliverySnapshot.value,
);

// dirty-трекінг (профіль + доставка)
function pickProfileForDirty(p = {}) {
  // Телефону тут НЕМАЄ навмисно: він більше не зберігається кнопкою
  // «Зберегти», а лише фактом підтвердження по SMS. Поки він був тут, саме
  // переформатування директивою `v-phone-ua` (вона пише назад у v-model)
  // робило форму вічно «брудною».
  return {
    displayName: p.displayName || "",
    instagram: p.instagram || "",
  };
}
const initialSnapshot = ref("");
const currentSnapshot = computed(() =>
  JSON.stringify({
    profile: pickProfileForDirty(profile.value || {}),
    delivery: delivery.value,
  }),
);
const isDirty = computed(() => currentSnapshot.value !== initialSnapshot.value);
const isSaving = ref(false);

// OTP-модалка
const otp = ref({ open: false, phoneE164: "" });

/**
 * Email акаунта. Порожній у тих, хто зайшов тільки по телефону.
 *
 * `linkedEmail` — та сама пастка, що й `justVerifiedE164`: `linkWithCredential`
 * повертає ТОЙ САМИЙ екземпляр User, тож `user.value = cred.user` присвоює
 * посилання саме на себе і Vue нічого не перемальовує. Без цього після
 * прив'язки форма лишалась би формою «додати email».
 */
const linkedEmail = ref("");
const accountEmail = computed(
  () => auth.user?.email || linkedEmail.value || "",
);

const resetNote = ref("");
const resetError = ref("");
const resetSending = ref(false);

// ── Прив'язка email до телефонного акаунта ──────────────────────────────
const linkEmail = ref("");
const linkPassword = ref("");
const linkNote = ref("");
const linkError = ref("");
const linkSending = ref(false);

const canLinkEmail = computed(
  () => /.+@.+\..+/.test(linkEmail.value) && linkPassword.value.length >= 6,
);

async function submitLinkEmail() {
  linkNote.value = "";
  linkError.value = "";
  if (!canLinkEmail.value) return;

  linkSending.value = true;
  try {
    await auth.linkEmailPassword({
      email: linkEmail.value,
      password: linkPassword.value,
    });
    linkedEmail.value = linkEmail.value;
    linkPassword.value = "";
    linkNote.value = "Готово — тепер можна входити і через email.";
  } catch (e) {
    const code = String(e?.code || e?.message || "");
    if (code.includes("auth/email-already-in-use")) {
      linkError.value = "Цей email уже прив'язаний до іншого акаунта.";
    } else if (code.includes("auth/weak-password")) {
      linkError.value = "Пароль закороткий — мінімум 6 символів.";
    } else if (code.includes("auth/invalid-email")) {
      linkError.value = "Некоректний email.";
    } else if (code.includes("auth/requires-recent-login")) {
      // Firebase вимагає свіжий вхід для зміни способів входу.
      linkError.value =
        "З міркувань безпеки увійдіть заново, а потім додайте email.";
    } else {
      linkError.value = "Не вдалося додати email. Спробуйте пізніше.";
    }
  } finally {
    linkSending.value = false;
  }
}

/** Зміна пароля = той самий лист скидання. Власного UI для пароля не заводимо. */
async function requestPasswordReset() {
  resetNote.value = "";
  resetError.value = "";
  if (!accountEmail.value) return;

  resetSending.value = true;
  try {
    await auth.sendPasswordReset(accountEmail.value);
    resetNote.value = "Лист надіслано — перевірте пошту і теку «Спам».";
  } catch {
    resetError.value = "Не вдалося надіслати лист. Спробуйте пізніше.";
  } finally {
    resetSending.value = false;
  }
}

async function checkPhoneAvailableDetailed(phoneE164) {
  const fn = httpsCallable($functions, "isPhoneAvailable");
  const { data } = await fn({ phone: phoneE164 });
  return { available: !!data?.available, owns: !!data?.owns };
}

// збереження
async function save() {
  if (!isNameValid.value) return;
  // 🔵 не блокуємо телефон, якщо доставку не змінювали
  if (
    (deliveryReady.value && deliveryChanged.value && !deliveryValid.value) ||
    !isDirty.value
  )
    return;
  // Телефон «Зберегти» не чіпає: він зберігається підтвердженням по SMS.
  await persistProfile();
}

/**
 * Добровільна верифікація: прив'язує номер до акаунта як identity
 * (updatePhoneNumber у модалці) і відкриває вхід по SMS.
 *
 * Унікальність перевіряємо ТІЛЬКИ тут: два акаунти можуть мати однаковий
 * контакт доставки (родина, спільний номер), але не однакову identity.
 */
async function startPhoneVerification() {
  const e164 = toE164(form.value.phone);
  if (!e164) return;

  pError.value = "";
  try {
    const { available, owns } = await checkPhoneAvailableDetailed(e164);
    if (!available && !owns) {
      pError.value = "Цей номер вже використовується іншим акаунтом";
      return;
    }
  } catch (e) {
    // callable недоступна — не блокуємо: чужий номер усе одно відсіче бекенд
    console.warn("isPhoneAvailable failed:", e);
  }
  otp.value = { open: true, phoneE164: e164 };
}

async function persistProfile() {
  isSaving.value = true;
  try {
    profile.value = profile.value || {};
    // ДЗЕРКАЛО верифікованого номера, а не вміст поля. Інакше «Зберегти»
    // записало б сюди щойно набраний, ще не підтверджений номер — і в базі
    // знову з'явилося б третє, самостійне значення телефону.
    //
    // Порожнє НЕ пишемо НІКОЛИ: `saveProfile` мерджить, тож `phone: ""` стер
    // би збережений номер — а по ньому `functions/src/promos.js` шукає ключ
    // чорного списку welcome10. Одне натискання «Зберегти» в акаунті без
    // верифікованого номера випускало б людину зі списку.
    const verified = justVerifiedE164.value || auth.user?.phoneNumber || "";
    if (verified) {
      profile.value.phone = maskFromE164(verified);
      profile.value.phoneE164 = verified;
    }
    profile.value.shipping = { ...delivery.value };
    await saveProfile();

    // 🔵 оновлюємо базові стани після збереження
    initialDeliverySnapshot.value = JSON.stringify(delivery.value);
    initialSnapshot.value = currentSnapshot.value;
  } finally {
    isSaving.value = false;
  }
}

// OTP події
async function onOtpVerified() {
  otp.value.open = false;
  justVerifiedE164.value = otp.value.phoneE164 || "";
  await persistProfile();
}
function onOtpCancelled() {
  // Відкоту НЕ робимо: поле просто лишається з тим, що людина набрала, а в
  // базу без підтвердження нічого й не пішло. Раніше OTP був єдиним шляхом
  // зберегти телефон, тож скасування мусило відкотити поле.
  otp.value.open = false;
}

// логаут
async function doLogout() {
  await auth.logout();
  router.replace("/");
}

// вирівнювання правої колонки
const leftCardRef = ref(null);
const leftCardHeight = ref(0);
let leftRo;
const rightMaxHeight = computed(() =>
  leftCardHeight.value ? `${leftCardHeight.value}px` : undefined,
);
function measureLeft() {
  if (leftCardRef.value) leftCardHeight.value = leftCardRef.value.offsetHeight;
}

onMounted(async () => {
  await fetchProfile();

  // Телефон: ВЕРИФІКОВАНИЙ → збережений у профілі → порожньо.
  //
  // Верифікований іде ПЕРШИМ навмисно. Це поле — власний номер людини, і воно
  // не повинно показувати контакт отримувача з чужого замовлення. Порядок
  // «профіль → Auth» разом із записом контакту в `profile.phone` з чекауту
  // давав пастку: відправив посилку другові — і в профілі назавжди чужий
  // номер, якого людина не може підтвердити по SMS.
  // Чекаут більше в `profile.phone` не пише (див. useCheckoutForm), але в
  // частини акаунтів там уже лежить те, що він встиг записати.
  // ТІЛЬКИ верифікований номер або порожньо. Ніяких фолбеків на збережене:
  // поле означає «номер, яким ви входите», і показувати тут щось інше —
  // саме та пастка, через яку в профілі оселявся чужий номер із замовлення.
  const ownPhone = auth.user?.phoneNumber;
  form.value.phone = ownPhone ? maskFromE164(ownPhone) : "";

  // доставка з профілю
  const s = profile.value?.shipping || {};
  delivery.value = {
    deliveryType: s.deliveryType || "np_branch",
    city: s.city || "",
    cityRef: s.cityRef || "",
    line1: s.line1 || "",
    warehouseRef: s.warehouseRef || "",
    warehouseName: s.warehouseName || "",
  };
  ui.stop("profile-load");

  // 🔵 фіксуємо стартовий стан
  initialDeliverySnapshot.value = JSON.stringify(delivery.value);
  phoneTouched.value = false;
  nameTouched.value = false;
  initialSnapshot.value = currentSnapshot.value;
  deliveryReady.value = true;

  await nextTick();
  measureLeft();
  if (typeof ResizeObserver !== "undefined" && leftCardRef.value) {
    leftRo = new ResizeObserver(() => measureLeft());
    leftRo.observe(leftCardRef.value);
  }

  window.addEventListener("resize", measureLeft);
});

onBeforeUnmount(() => {
  if (leftRo) leftRo.disconnect();
  window.removeEventListener("resize", measureLeft);
});
</script>
